import {
  generateAvailabilityItems,
  getItems,
  getParticipants,
  GetRenderGroups,
  GroupObj,
  HierarchyObj,
  ItemObj,
  TimelineContainer,
} from 'components/TimeLine'
import { TFunction } from 'i18next'
import { Proving } from 'model'
import moment, { Moment } from 'moment'
import * as React from 'react'
import Timeline, {
  DateHeader,
  ReactCalendarItemRendererProps,
  TimelineHeaders,
} from 'react-calendar-timeline'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { Section } from 'views/ProvingDetailsView/Layout'
import { FilterObject } from './AppletViewExtensions'
import { TimeLineLeftBarGroupColors } from './Styles'

export interface TimeLineOptions {
  fgroup: GroupObj[] | null
  fitems: ItemObj[] | null
  fdateFrom: Moment | null
  fdateTo: Moment | null
  resIdLookup: string[] | null
  // resIdLookup:{id:string,category:string,type:string}[]|null
}
// indexTo is included in the current mainCategory.
export interface MainCategorySpan {
  indexFrom: number
  indexTo: number
}
type MainCategorySpanMapping = Record<number, MainCategorySpan>

const getTimeLineProps = (
  provings: Proving[] | undefined,
  groupHierarchy: HierarchyObj[] | undefined,
  filter: FilterObject,
  t: TFunction
): TimeLineOptions => {
  const props = {
    fgroup: null,
    fitems: null,
    fdateFrom: null,
    fdateTo: null,
    resIdLookup: null,
  } as TimeLineOptions

  const { resourceGroups, groupsLookUp } = GetRenderGroups(
    groupHierarchy,
    filter.resources,
    t
  )

  const { ritems, dateFrom, dateTo } = getItems(
    provings,
    groupsLookUp,
    filter,
    t
  )

  const { groups, items } = getParticipants(
    provings,
    filter.persons,
    resourceGroups,
    ritems,
    t
  )

  props.fgroup = groups
  props.fitems = items
  props.fdateFrom = dateFrom
  props.fdateTo = dateTo
  props.resIdLookup = Object.keys(groupsLookUp)
  return props
}

export const BookingsAppletTimeLine: React.FunctionComponent<{
  provings: Proving[] | undefined
  groupHierarchy: HierarchyObj[] | undefined
  filter: FilterObject
}> = ({ provings, groupHierarchy, filter, ...props }) => {
  const { t } = useTranslation()
  const router = useRouter()
  router.setOption('queryParamsMode', 'strict')
  const timeLineProps = getTimeLineProps(provings, groupHierarchy, filter, t)
  const propGroups = timeLineProps.fgroup ? timeLineProps.fgroup : []
  const resAvailabilityByGroup: MainCategorySpanMapping = {}
  resAvailabilityByGroup[0] = { indexFrom: 0, indexTo: 0 }
  if (propGroups.length !== 0) {
    let currentMainGroupId = 0
    propGroups.forEach((gr) => {
      if (gr.type && TimeLineLeftBarGroupColors[gr.type] === '#5291DF') {
        // this is a main category check based on the color styling.

        resAvailabilityByGroup[gr.id] = { indexFrom: gr.id, indexTo: gr.id }
        currentMainGroupId = gr.id
      } else {
        resAvailabilityByGroup[currentMainGroupId].indexTo = gr.id
      }
    })
  }

  let availabilityTimeStart = moment()
  let availabilityTimeEnd = moment(availabilityTimeStart).add(30, 'd')
  // Duration of the res.availability display is set to +-30 days of the earliet and latest proving accordingly
  if (timeLineProps.fdateFrom && timeLineProps.fdateTo) {
    availabilityTimeStart = moment(timeLineProps.fdateFrom).subtract(30, 'd')
    availabilityTimeEnd = moment(timeLineProps.fdateTo).add(30, 'd')
  }

  const availabilityItems = generateAvailabilityItems(
    availabilityTimeStart,
    availabilityTimeEnd,
    resAvailabilityByGroup,
    timeLineProps.fitems ? timeLineProps.fitems : [],
    propGroups
  )
  const defaultItems = timeLineProps.fitems ? [...timeLineProps.fitems] : []
  const defaultGroups = timeLineProps.fgroup ? [...timeLineProps.fgroup] : []
  const itemRenderer = (itemprops: ReactCalendarItemRendererProps<ItemObj>) => {
    const { item, getItemProps, getResizeProps, itemContext } = itemprops
    const { left: leftResizeProps, right: rightResizeProps } = getResizeProps()
    const redeclareItemProps: any = getItemProps(item.itemProps)
    redeclareItemProps.title = item.title

    return (
      <div {...redeclareItemProps}>
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ''}
        <span
          className="rct-item-content"
          style={{
            maxHeight: `${itemContext.dimensions.height}`,
          }}
        >
          {String(item.title.split('\n')[0])}
        </span>
        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ''}
      </div>
    )
  }

  const groupRenderer = (item: any) => {
    return (
      <div
        style={{
          backgroundColor: TimeLineLeftBarGroupColors[item.group.type]
            ? TimeLineLeftBarGroupColors[item.group.type]
            : '#FFF',
          textAlign: 'center',
          margin: '0 -4px',
        }}
      >
        <span title={item.group.title} className="title">
          {item.group.type === 'Building'
            ? `${
                t(`field.applet.resources.buildings`) + ': ' + item.group.title
              }`
            : `${item.group.title}`}
        </span>
      </div>
    )
  }
  return (
    <>
      <Section label={t(`field.applet.timeline.label`)}>
        {timeLineProps &&
          timeLineProps.fgroup &&
          timeLineProps.fgroup.length > 0 && (
            <TimelineContainer>
              <Timeline
                minZoom={86400}
                maxZoom={365 * 24 * 86400 * 52}
                sidebarWidth={200}
                sidebarContent={<div>Above The Left</div>}
                groupRenderer={groupRenderer}
                itemRenderer={itemRenderer}
                timeSteps={{
                  second: 1,
                  minute: 1,
                  hour: 1,
                  day: 1,
                  month: 1,
                  year: 1,
                }}
                onItemDoubleClick={(item) => {
                  if (
                    defaultItems.length !== 0 &&
                    defaultGroups.length !== 0 &&
                    timeLineProps.resIdLookup
                  ) {
                    const selectedItem = defaultItems.find(
                      (defItem) => defItem.id === item
                    )
                    if (selectedItem) {
                      if (selectedItem.itemProps) {
                        router.navigate('provings.details', {
                          id: selectedItem.itemProps.resDetails.erpid,
                        })
                      }
                    }
                  }
                }}
                groups={timeLineProps.fgroup ? timeLineProps.fgroup : []}
                items={
                  timeLineProps.fitems
                    ? [...timeLineProps.fitems, ...availabilityItems]
                    : []
                }
                canMove={false}
                defaultTimeStart={
                  timeLineProps.fdateFrom
                    ? moment(timeLineProps.fdateFrom).subtract(2, 'd')
                    : moment()
                }
                defaultTimeEnd={
                  timeLineProps.fdateTo
                    ? moment(timeLineProps.fdateTo).add(2, 'd')
                    : moment()
                }
              >
                <TimelineHeaders>
                  <DateHeader unit="primaryHeader" />
                  <DateHeader
                    unit="week"
                    labelFormat={(time) => {
                      const weeknumber = moment(time[0]).week()
                      return `KW ${weeknumber}`
                    }}
                  />
                  <DateHeader unit="day" />
                </TimelineHeaders>
              </Timeline>
            </TimelineContainer>
          )}
      </Section>
    </>
  )
}
